import { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import { trackError } from '../services/rumService';
import type {
  MonthlyData,
  Project,
  YearlyData,
  Organization,
} from '../types/shared';

// Re-export for backward compatibility
export type { YearlyData };

export interface DashboardData {
  carbonData: MonthlyData[];
  projects: Project[];
  organization: Organization | null;
  loading: boolean;
  error: string | null;
  selectedYear: number;
  setSelectedYear: (year: number) => void;
  yearlyData: YearlyData[];
  availableYears: number[];
  selectedYearData: MonthlyData[];
  selectedYearSummary: YearlyData | null;
}

export const useDashboardData = (): DashboardData => {
  const [carbonData, setCarbonData] = useState<MonthlyData[]>([]);
  const [yearlyData, setYearlyData] = useState<YearlyData[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedYear, setSelectedYear] = useState<number>(
    new Date().getFullYear()
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch carbon impact data (which now includes projects) and organization data
        const [carbonResult, organizationResult] = await Promise.all([
          apiService.getCarbonImpactData(),
          apiService.getUserOrganization(),
        ]);

        setCarbonData(carbonResult.monthly);
        setYearlyData(carbonResult.annual);
        setProjects(carbonResult.projects);
        setOrganization(organizationResult);

        // Set the most recent year with data as default
        if (carbonResult.monthly.length > 0) {
          const availableYears = Array.from(
            new Set(carbonResult.monthly.map(item => item.year))
          ).sort((a, b) => b - a);
          setSelectedYear(availableYears[0]);
        }
      } catch (err) {
        const isInstanceOfError = err instanceof Error;

        const errorMessage = isInstanceOfError
          ? err.message
          : 'An unknown error occurred';
        setError(errorMessage);

        if (isInstanceOfError) {
          trackError(err, 'useDashboardData', {
            operation: 'fetchDashboardData',
            timestamp: Date.now(),
          });
        }

        if (process.env.NODE_ENV === 'development') {
          console.error('Dashboard data fetch error:', err);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Use backend-calculated annual data (no more redundant calculations)
  const availableYears = Array.from(
    new Set(carbonData.filter(item => item?.year).map(item => item.year))
  ).sort((a, b) => b - a);
  const selectedYearData = carbonData
    ? carbonData.filter(item => item?.year === selectedYear)
    : [];
  const selectedYearSummary = yearlyData
    ? yearlyData.find(item => item?.year === selectedYear) || null
    : null;

  return {
    carbonData,
    projects,
    organization,
    loading,
    error,
    selectedYear,
    setSelectedYear,
    yearlyData,
    availableYears,
    selectedYearData,
    selectedYearSummary,
  };
};
